import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PurchaseNavButton } from './CheckoutFlow';
import { API_BASE } from '../lib/apiBase';
import analytics from '../lib/analytics';

const TIERS = [
  {
    id: 'simple',
    name: 'Simple',
    price: 99,
    blurb: 'Straightforward petitions (e.g. family I-130, F-1 change of status, DV).',
  },
  {
    id: 'mid',
    name: 'Mid',
    price: 149,
    blurb: 'Moderate complexity (e.g. AOS, PERM, O-1, asylum).',
  },
  {
    id: 'complex',
    name: 'Complex',
    price: 199,
    blurb: 'Evidence-heavy categories (EB-2 NIW, EB-1A).',
  },
];

export default function AttorneyAssistPurchase() {
  const [searchParams] = useSearchParams();
  const [tier, setTier] = useState('simple');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pathwayHint, setPathwayHint] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const canceled = searchParams.get('canceled') === '1';

  useEffect(() => {
    analytics.trackPageView('attorney-assist');
    document.title = 'Attorney Assist · ImmigrateSmart';
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Please upload your draft petition or document (PDF or Word).');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('client_name', name.trim());
      fd.append('client_email', email.trim());
      fd.append('tier', tier);
      if (pathwayHint.trim()) fd.append('pathway_hint', pathwayHint.trim());
      fd.append('draft', file);

      const res = await fetch(`${API_BASE}/api/attorney-assist/checkout`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Checkout failed');
      }
      if (!data.checkout_url) throw new Error('No checkout URL returned');
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Checkout failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F3F0]">
      <nav className="bg-[#3A3A3F] px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <a href="/" className="flex items-center text-white hover:opacity-90 min-w-0">
            <span className="wordmark-mark" aria-hidden="true"></span>
            <span className="wordmark text-lg text-white truncate">ImmigrateSmart</span>
          </a>
          <div className="flex items-center gap-3 shrink-0">
            <PurchaseNavButton />
            <a href="/#attorney-assist" className="text-white/80 hover:text-[#D4A017] text-sm">
              About
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <p className="text-[10px] tracking-[0.16em] uppercase text-[#B8860B] font-semibold mb-2">
          UOA Legal review
        </p>
        <h1 className="font-cormorant text-4xl sm:text-5xl text-[#222226] mb-3">
          Attorney Assist
        </h1>
        <p className="text-[#3F3C38] text-sm leading-relaxed mb-8 max-w-xl">
          Pay once, upload your draft, and get a FedEx-style tracking link. UOA Legal reviews your
          file with a 48-hour delivery target. No account required.
        </p>

        {canceled && (
          <p className="mb-6 text-sm text-[#3F3C38] border border-[#222226]/15 p-3">
            Checkout was canceled. You can try again below.
          </p>
        )}

        <form onSubmit={submit} className="space-y-8">
          <fieldset>
            <legend className="text-xs tracking-widest uppercase text-[#B8860B] font-semibold mb-4">
              Choose tier
            </legend>
            <div className="space-y-3">
              {TIERS.map((t) => (
                <label
                  key={t.id}
                  className={`flex gap-4 p-4 border cursor-pointer transition-colors ${
                    tier === t.id
                      ? 'border-[#D4A017] bg-[#D4A017]/10'
                      : 'border-[#222226]/15 hover:border-[#222226]/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="tier"
                    value={t.id}
                    checked={tier === t.id}
                    onChange={() => setTier(t.id)}
                    className="mt-1"
                  />
                  <span className="flex-1">
                    <span className="flex justify-between gap-2">
                      <span className="font-semibold text-[#222226]">{t.name}</span>
                      <span className="font-cormorant text-xl text-[#222226]">${t.price}</span>
                    </span>
                    <span className="block text-sm text-[#3F3C38] mt-1">{t.blurb}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase text-[#3F3C38]/70 mb-2">
                Full name
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-[#222226]/20 bg-white text-[#222226]"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-[#3F3C38]/70 mb-2">
                Email
              </label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-[#222226]/20 bg-white text-[#222226]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase text-[#3F3C38]/70 mb-2">
              Pathway (optional)
            </label>
            <input
              value={pathwayHint}
              onChange={(e) => setPathwayHint(e.target.value)}
              placeholder="e.g. EB-2 NIW"
              className="w-full px-4 py-3 border border-[#222226]/20 bg-white text-[#222226]"
            />
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase text-[#3F3C38]/70 mb-2">
              Upload draft (PDF, DOC, or DOCX — max 25 MB)
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-[#3F3C38]"
              required
            />
          </div>

          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-10 py-3 bg-[#D4A017] text-[#222226] font-semibold tracking-wide hover:bg-[#E8B41F] disabled:opacity-50"
          >
            {loading ? 'Redirecting to payment…' : 'Continue to payment'}
          </button>
        </form>

        <p className="mt-10 text-xs text-[#3F3C38]/60 leading-relaxed">
          After payment you receive a unique tracking link at{' '}
          <code className="text-[#222226]">/track/…</code> — bookmark it. Reviewed by UOA Legal only.
          ImmigrateSmart provides document-preparation tools; attorney review is a separate engagement
          with counsel. Not legal advice.{' '}
          <Link to="/legal/disclaimer" className="text-[#B8860B] hover:underline">
            Disclaimer
          </Link>
        </p>
      </main>
    </div>
  );
}
